import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Sidebar from '../../components/shared/Sidebar'
import { Calendar, Users, CloudRain, DollarSign, Bell, UserCheck, BarChart2, Link, Tag, ShieldCheck, CreditCard, CheckCircle, X } from 'lucide-react'
import SubscriptionGate from '../SubscriptionGate'
import SchoolSetup from './SchoolSetup'
import { useAuth } from '../../hooks/useAuth'
import { supabase } from '../../lib/supabase'
import AdminSchedule from './AdminSchedule'
import AdminInstructors from './AdminInstructors'
import AdminInstructorApprovals from './AdminInstructorApprovals'
import AdminPayroll from './AdminPayroll'
import AdminWeather from './AdminWeather'
import AdminAnalytics from './AdminAnalytics'
import AdminPricing from './AdminPricing'
import AdminStripeConnect from './AdminStripeConnect'

const NAV = [
  { type: 'section', label: 'Operations' },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'instructors', label: 'Instructors', icon: Users },
  { id: 'approvals', label: 'Approvals', icon: UserCheck },
  { type: 'section', label: 'Insights & Tools' },
  { id: 'pricing', label: 'Pricing', icon: Tag },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'analytics', label: 'Analytics', icon: BarChart2 },
  { id: 'payroll', label: 'Payroll', icon: DollarSign },
  { id: 'weather', label: 'Weather', icon: CloudRain },
]

export default function AdminApp() {
  const navigate = useNavigate()
  const { user, refreshProfile } = useAuth()
  const [page, setPage] = useState('schedule')
  const [subscribed, setSubscribed] = useState(null)
  const [hasSchool, setHasSchool] = useState(null)
  const [platformApproved, setPlatformApproved] = useState(null)
  const [checking, setChecking] = useState(true)
  const [pendingCount, setPendingCount] = useState(0)
  const [pendingAdminCount, setPendingAdminCount] = useState(0)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [schoolName, setSchoolName] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [stripeBanner, setStripeBanner] = useState(null) // 'success' | 'incomplete' | 'error' | null
  const [chargesEnabled, setChargesEnabled] = useState(null)
  const [planTier, setPlanTier] = useState('pro')
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!user) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('subscribed') === 'true') {
      activateSubscription()
    } else {
      checkStatus()
    }
    // Handle Stripe Connect return params
    const stripeParam = params.get('stripe')
    if (stripeParam) {
      setStripeBanner(stripeParam)
      if (stripeParam === 'success') setPage('payments')
      window.history.replaceState({}, '', '/admin')
    }
  }, [user])

  const checkStatus = async () => {
    const { data } = await supabase.from('profiles').select('subscribed, school_id, platform_approved, plan_tier').eq('id', user.id).single()
    // Owner email is always approved regardless of db value
    const isOwner = user?.email === 'drewbacca15@gmail.com'
    setPlatformApproved(isOwner || data?.platform_approved !== false)
    setSubscribed(data?.subscribed === true)
    setHasSchool(!!data?.school_id)
    setPlanTier(data?.plan_tier || 'pro')
    if (data?.school_id) {
      const { count } = await supabase.from('instructors').select('id', { count: 'exact' }).eq('school_id', data.school_id).eq('approved', false)
      setPendingCount(count || 0)
      const { data: school } = await supabase.from('schools').select('name, charges_enabled').eq('id', data.school_id).single()
      setSchoolName(school?.name || '')
      setChargesEnabled(school?.charges_enabled || false)
    }
    // Load pending platform admin count for the owner
    if (isOwner) {
      const { count } = await supabase.from('profiles').select('id', { count: 'exact' }).eq('role', 'admin').eq('platform_approved', false)
      setPendingAdminCount(count || 0)
    }
    setChecking(false)
  }

  const activateSubscription = async () => {
    const tier = localStorage.getItem('pendingTier') || 'pro'
    localStorage.removeItem('pendingTier')
    await supabase.from('profiles').update({ subscribed: true, subscription_status: 'active', plan_tier: tier }).eq('id', user.id)
    setPlanTier(tier)
    setSubscribed(true)
    setChecking(false)
    window.history.replaceState({}, '', '/admin')
    refreshProfile()
    const { data } = await supabase.from('profiles').select('school_id').eq('id', user.id).single()
    setHasSchool(!!data?.school_id)
    if (data?.school_id) {
      const { data: school } = await supabase.from('schools').select('name').eq('id', data.school_id).single()
      setSchoolName(school?.name || '')
    }
  }

  const handleCancelSubscription = async () => {
    setCancelling(true)
    const { error } = await supabase
      .from('profiles')
      .update({ subscribed: false, subscription_status: 'cancelled' })
      .eq('id', user.id)
    if (error) {
      alert('Something went wrong. Please try again.')
      setCancelling(false)
    } else {
      setShowCancelConfirm(false)
      setSubscribed(false)
    }
  }

  const getSignupLink = () => {
    const slug = schoolName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
    return `${window.location.origin}/join/${slug}`
  }

  const copySignupLink = () => {
    navigator.clipboard.writeText(getSignupLink())
    setLinkCopied(true)
    setTimeout(() => setLinkCopied(false), 2500)
  }

  const goTo = (id) => { setPage(id); navigate(`/admin/${id === 'schedule' ? '' : id}`) }
  const navWithBadge = NAV.map(n => {
    if (n.id === 'approvals' && pendingCount > 0) return { ...n, badge: pendingCount }
    if (planTier === 'starter' && (n.id === 'analytics' || n.id === 'payroll')) return { ...n, locked: true }
    if (planTier === 'standard' && n.id === 'analytics') return { ...n, locked: true }
    return n
  })

  const PAGES = {
    schedule: <AdminSchedule />,
    instructors: <AdminInstructors />,
    approvals: <AdminInstructorApprovals />,
    pricing: <AdminPricing />,
    payments: <AdminStripeConnect />,
    analytics: <AdminAnalytics />,
    payroll: <AdminPayroll />,
    weather: <AdminWeather />,
  }

  if (checking) return <div className="loading-screen"><div className="spinner" style={{ width: 40, height: 40, borderWidth: 3 }} /><p>Loading...</p></div>

  if (!platformApproved) return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        <div style={{ fontSize: 56, marginBottom: '1.25rem' }}>⏳</div>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', color: 'white', marginBottom: '0.75rem' }}>
          Awaiting Approval
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1.5rem' }}>
          Your WaveSync account is pending approval. You'll be notified once access has been granted.
        </p>
        <div style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '1rem 1.25rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
          Typically approved within 24 hours. If you have questions, reach out directly to your WaveSync contact.
        </div>
        <button onClick={() => { supabase.auth.signOut(); window.location.href = '/' }} style={{ marginTop: '1.5rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.5)', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'DM Sans, sans-serif' }}>
          Sign Out
        </button>
      </div>
    </div>
  )

  if (!subscribed) return <SubscriptionGate onSuccess={activateSubscription} />
  if (!hasSchool) return <SchoolSetup onComplete={() => setHasSchool(true)} />

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#F0EDE4' }}>
      <Sidebar items={navWithBadge} currentPage={page} onNavigate={goTo} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top bar */}
        <div style={{
          background: 'white',
          borderBottom: '1px solid rgba(11,30,61,0.08)',
          boxShadow: '0 1px 4px rgba(11,30,61,0.06)',
          flexShrink: 0,
          paddingTop: 'env(safe-area-inset-top)',
        }}>
        <div style={{
          height: 56, display: 'flex', alignItems: 'center',
          padding: isMobile ? '0 16px 0 64px' : '0 24px',
          gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
              {NAV.find(n => n.id === page)?.label || 'Schedule'}
            </h1>
            {schoolName && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: 1 }}>
                {schoolName}
              </div>
            )}
          </div>

          {/* Platform admin button — only visible to owner */}
          {user?.email === 'drewbacca15@gmail.com' && (
            <button
              onClick={() => navigate('/super-admin')}
              style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 5,
                background: pendingAdminCount > 0 ? 'rgba(232,97,74,0.08)' : 'rgba(11,30,61,0.05)',
                border: `1px solid ${pendingAdminCount > 0 ? 'rgba(232,97,74,0.3)' : 'rgba(11,30,61,0.12)'}`,
                color: pendingAdminCount > 0 ? 'var(--coral)' : 'var(--text-mid)',
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              <ShieldCheck size={12} />
              {!isMobile && 'Platform Admin'}
              {pendingAdminCount > 0 && (
                <span style={{
                  background: 'var(--coral)', color: 'white',
                  borderRadius: '50%', width: 16, height: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.6rem', fontWeight: 700,
                }}>
                  {pendingAdminCount}
                </span>
              )}
            </button>
          )}

          {/* Copy signup link — hide label on mobile */}
          {schoolName && (
            <button
              onClick={copySignupLink}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: linkCopied ? 'rgba(14,170,164,0.1)' : 'rgba(11,30,61,0.05)',
                border: `1px solid ${linkCopied ? 'rgba(14,170,164,0.3)' : 'rgba(11,30,61,0.12)'}`,
                color: linkCopied ? 'var(--teal)' : 'var(--text-mid)',
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}
            >
              <Link size={12} />
              {isMobile ? '' : linkCopied ? 'Copied!' : 'Signup Link'}
              {isMobile && (linkCopied ? '✓' : '')}
            </button>
          )}

          {/* Payment status dot — only show once school is loaded */}
          {chargesEnabled !== null && (
            <button
              onClick={() => goTo('payments')}
              title={chargesEnabled ? 'Payments connected' : 'Payments not set up'}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                background: chargesEnabled ? 'rgba(14,170,164,0.08)' : 'rgba(212,168,75,0.12)',
                border: `1px solid ${chargesEnabled ? 'rgba(14,170,164,0.25)' : 'rgba(212,168,75,0.35)'}`,
                color: chargesEnabled ? 'var(--teal)' : '#a07a1a',
                padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
                whiteSpace: 'nowrap',
              }}
            >
              <CreditCard size={12} />
              {!isMobile && (chargesEnabled ? 'Payments On' : 'Setup Payments')}
            </button>
          )}

          {/* Bell */}
          <button style={{
            position: 'relative', width: 36, height: 36, borderRadius: 8,
            background: 'rgba(11,30,61,0.05)', border: '1px solid rgba(11,30,61,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: 'var(--text-mid)',
          }}>
            <Bell size={15} />
            {pendingCount > 0 && (
              <span style={{
                position: 'absolute', top: 6, right: 6, width: 7, height: 7,
                background: '#E8614A', borderRadius: '50%',
              }} />
            )}
          </button>

          {/* Upgrade button — only for non-Pro */}
          {planTier !== 'pro' && (
            <button
              onClick={() => setShowUpgradeModal(true)}
              style={{
                background: 'rgba(14,170,164,0.1)', border: '1px solid rgba(14,170,164,0.35)',
                color: 'var(--teal)', padding: isMobile ? '6px 10px' : '6px 14px',
                borderRadius: 8, cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
              }}
            >
              {isMobile ? '↑' : 'Upgrade Plan'}
            </button>
          )}

          {/* Cancel plan — icon only on mobile */}
          <button
            onClick={() => setShowCancelConfirm(true)}
            style={{
              background: 'transparent', border: '1px solid rgba(232,97,74,0.3)',
              color: 'var(--coral)', padding: isMobile ? '6px 10px' : '6px 14px',
              borderRadius: 8, cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 600,
              fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap',
            }}
          >
            {isMobile ? '✕' : 'Cancel Plan'}
          </button>
        </div>
        </div>

        {/* Stripe return banner */}
        {stripeBanner && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '10px 20px', flexShrink: 0,
            background: stripeBanner === 'success' ? 'rgba(14,170,164,0.1)' : 'rgba(212,168,75,0.12)',
            borderBottom: `1px solid ${stripeBanner === 'success' ? 'rgba(14,170,164,0.25)' : 'rgba(212,168,75,0.3)'}`,
          }}>
            {stripeBanner === 'success'
              ? <CheckCircle size={16} color="var(--teal)" />
              : <CreditCard size={16} color="#a07a1a" />
            }
            <span style={{ flex: 1, fontSize: '0.825rem', fontWeight: 600, color: stripeBanner === 'success' ? 'var(--teal)' : '#a07a1a' }}>
              {stripeBanner === 'success'
                ? '🎉 Your bank account is connected! You can now accept lesson payments.'
                : stripeBanner === 'incomplete'
                ? 'Stripe setup wasn\'t completed. Click "Continue Setup" to finish connecting your account.'
                : 'Something went wrong with Stripe setup. Please try again from the Payments page.'}
            </span>
            <button
              onClick={() => setStripeBanner(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-mid)', display: 'flex', alignItems: 'center' }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '24px' }}>
          <div key={page} className="page-enter">
            {PAGES[page] || PAGES.schedule}
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: '#0B1E3D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '36px 28px', maxWidth: 480, width: '100%', animation: 'modalIn 0.22s cubic-bezier(0.22,0.61,0.36,1) both' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#fff', fontSize: 20, margin: '0 0 6px' }}>Upgrade Your Plan</h2>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 13, margin: 0 }}>
                  Currently on <span style={{ color: 'var(--teal)', fontWeight: 600, textTransform: 'capitalize' }}>{planTier}</span>
                </p>
              </div>
              <button onClick={() => setShowUpgradeModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {planTier === 'starter' && (
                <div style={{ background: 'rgba(14,170,164,0.08)', border: '1px solid rgba(14,170,164,0.25)', borderRadius: 14, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#fff', fontSize: 15 }}>Standard</div>
                      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Up to 14 instructors · AI Matching · Payroll</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 900, color: '#fff' }}>$700</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>difference owed</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { localStorage.setItem('pendingTier', 'standard'); window.location.href = 'https://buy.stripe.com/fZu9AU9Ke8GievI0Wl7Vm06' }}
                    style={{ width: '100%', padding: '10px', background: 'var(--teal)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                  >
                    Upgrade to Standard →
                  </button>
                </div>
              )}

              <div style={{ background: 'rgba(232,97,74,0.08)', border: '1px solid rgba(232,97,74,0.25)', borderRadius: 14, padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: 15 }}>Pro</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>Unlimited instructors · Full analytics · Everything</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 900, color: '#fff' }}>
                      {planTier === 'starter' ? '$1,600' : '$900'}
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>difference owed</div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    localStorage.setItem('pendingTier', 'pro')
                    window.location.href = planTier === 'starter'
                      ? 'https://buy.stripe.com/4gM5kE3lQbSugDQ5cB7Vm08'
                      : 'https://buy.stripe.com/dRm14oe0u1dQ1IW48x7Vm07'
                  }}
                  style={{ width: '100%', padding: '10px', background: 'var(--coral)', border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                >
                  Upgrade to Pro →
                </button>
              </div>
            </div>

            <p style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.25)', marginTop: 20, marginBottom: 0 }}>
              Secured by Stripe · Questions? drewmirarchi8@gmail.com
            </p>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: '#0B1E3D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '40px 32px', maxWidth: 420, width: '100%', textAlign: 'center', animation: 'modalIn 0.22s cubic-bezier(0.22,0.61,0.36,1) both' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>😔</div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#fff', marginBottom: 10, fontSize: 22 }}>Cancel WaveSync {planTier === 'starter' ? 'Starter' : 'Pro'}?</h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, lineHeight: 1.7, marginBottom: 28 }}>
              You'll lose access to scheduling, instructor management, weather, and all platform features immediately.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowCancelConfirm(false)} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                Keep Plan
              </button>
              <button onClick={handleCancelSubscription} disabled={cancelling} style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: '#E8614A', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: cancelling ? 0.6 : 1, fontFamily: 'DM Sans, sans-serif' }}>
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
